import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import * as _ from 'lodash-es';

import { AppService } from '../../../../app.service';
import { GlobalService } from '../../../../global/services/global.service';
import {
  AdminGridConfig,
  AdminGridCallbackEvent,
  AdminGridDirective,
} from '../../../shared/admin-grid.directive';
import { CreateSystemAccountModalComponent } from './create-system-account-modal/create-system-account-modal.component';
import { EditSystemAccountModalComponent } from './edit-system-account-modal/edit-system-account-modal.component';

/**
 * Replicates AngularJS `SystemToFormMappingCtrl` (system-mapping.html + SystemToFormMappingCtrl.js).
 *
 * Filter bar: refresh icon, tax year, Filter, "Add New System Account".
 * Grid: action_id=30256, grid_id=1054 (loadGridJson endpoint from masterData.js state config).
 * Row click  → opens EditSystemAccountModalComponent.
 * "Add New" → opens CreateSystemAccountModalComponent.
 */
@Component({
    selector: 'gtw-system-mapping',
    templateUrl: './system-mapping.component.html',
    styleUrls: ['./system-mapping.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SystemMappingComponent implements OnInit, OnDestroy {
  @ViewChild(AdminGridDirective) adminGrid!: AdminGridDirective;

  gridConfig!: AdminGridConfig;
  loading = false;

  taxYears: any[] = [];
  selectedTaxYear: any = null;
  /** Derived from the selected tax year — not exposed as a filter control. */
  selectedScenario: any = null;

  private readonly ACTION_ID = 30256;
  private readonly GRID_ID = '1054';
  private readonly JCD_KEY = 250;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private appService: AppService,
    private globalService: GlobalService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.initTaxYears();
    this.gridConfig = {
      actionId: this.ACTION_ID,
      gridId: this.GRID_ID,
      extraParams: this.buildExtraParams(),
      autoLoad: true,
      cacheKey: 'admin/master-data/system-mapping',
    };
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // -- Filter bar interactions ----------------------------------------------

  onTaxYearChange(): void {
    this.selectedScenario = this.resolveScenarioForTaxYear(this.selectedTaxYear);
    this.cdr.detectChanges();
  }

  refresh(): void {
    if (!this.gridConfig) return;
    this.gridConfig = { ...this.gridConfig, extraParams: this.buildExtraParams() };
    this.adminGrid?.clearCache();
    this.adminGrid?.refresh(this.buildExtraParams());
  }

  addNewSystemAccount(): void {
    const config = new MatDialogConfig();
    config.panelClass = 'admin-modal-panel';
    config.width = '95vw';
    config.maxWidth = '1400px';
    config.disableClose = true;
    config.data = {
      taxYear: this.selectedTaxYear?.value ?? this.globalService.global?.globalParams?.tax_year,
      scenario: this.selectedScenario,
    };

    const ref = this.dialog.open(CreateSystemAccountModalComponent, config);
    ref.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: any) => {
        if (result === 'saved') {
          this.refresh();
        }
      });
  }

  // -- Grid callback --------------------------------------------------------

  onGridCallback(event: AdminGridCallbackEvent): void {
    // Original `ctrlFn` reacts to row link click and opens the edit modal.
    const rowData = event.data?.rowData ?? event.data;
    if (!rowData) return;

    const config = new MatDialogConfig();
    config.panelClass = 'admin-modal-panel';
    config.width = '95vw';
    config.maxWidth = '1400px';
    config.disableClose = true;
    config.data = {
      rowData,
      taxYear: this.selectedTaxYear?.value ?? this.globalService.global?.globalParams?.tax_year,
      scenario: this.selectedScenario,
    };

    const ref = this.dialog.open(EditSystemAccountModalComponent, config);
    ref.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: any) => {
        if (result === 'saved') {
          this.refresh();
        }
      });
  }

  onLoadingChange(state: boolean): void {
    this.loading = state;
    this.cdr.detectChanges();
  }

  // -- Helpers --------------------------------------------------------------

  private initTaxYears(): void {
    const allTaxYears: any[] = [];
    _.forEach(this.appService.TAX_YEARS, (value: any) => {
      if (Array.isArray(value)) {
        value.forEach((v: any) => {
          if (v && typeof v === 'object' && v.value !== undefined) {
            allTaxYears.push(v);
          }
        });
      }
    });
    this.taxYears = allTaxYears;

    const gpTaxYear = this.globalService.global?.globalParams?.tax_year;

    this.selectedTaxYear =
      this.taxYears.find((ty: any) => String(ty.value) === String(gpTaxYear)) ||
      this.taxYears[0] ||
      null;

    this.selectedScenario = this.resolveScenarioForTaxYear(this.selectedTaxYear);
  }

  private resolveScenarioForTaxYear(taxYear: any): any {
    if (!taxYear) {
      return this.globalService.global?.globalParams?.scenario ?? '';
    }

    const scenarios: any[] = taxYear.scenarios ?? [];
    const gpScenario = this.globalService.global?.globalParams?.scenario;
    const matching =
      scenarios.find((s: any) => String(s.scenario) === String(taxYear.scenario)) ||
      scenarios.find((s: any) => String(s.scenario) === String(gpScenario)) ||
      scenarios[0];

    return matching?.scenario ?? taxYear.scenario ?? gpScenario ?? '';
  }

  private buildExtraParams(): Record<string, any> {
    return {
      tax_year: this.selectedTaxYear?.value ?? '',
      scenario: this.selectedScenario ?? '',
      jcd_key: this.JCD_KEY,
    };
  }
}
